import React, { useState, useCallback } from 'react'
import { useLazyQuery } from '@apollo/client'
import { SIDECAR_VERSIONS_QUERY } from '../query/gqlQueries'
import HistoryIcon from '@mui/icons-material/History'
import DownloadIcon from '@mui/icons-material/Download'
import ApertureIcon from '@mui/icons-material/CameraOutlined'

// The sidecars attached to one photo, each expandable into its saved versions.
//
// Two people editing the same raw both save a .xmp. The second to arrive
// becomes current and the first is archived rather than overwritten — but
// until now nothing surfaced that, so "your previous edits are kept" was a
// claim with no way to act on it. Expanding a sidecar lists every version,
// newest first, with who saved it and a download for that specific one.
//
// Versions are fetched only when a sidecar is expanded: a grid of 1000 photos
// must not fire a query per sidecar to render.

function formatBytes(bytes) {
  if (bytes == null) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1048576).toFixed(1)} MB`
}

function formatWhen(iso) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function OneSidecar({ sidecar, storeUrl, styles }) {
  const [open, setOpen] = useState(false)
  const [load, { data, loading, error }] = useLazyQuery(SIDECAR_VERSIONS_QUERY, {
    fetchPolicy: 'network-only',
  })

  const toggle = useCallback((e) => {
    e.stopPropagation()
    const next = !open
    setOpen(next)
    if (next && !data) load({ variables: { path: sidecar.path } })
  }, [open, data, load, sidecar.path])

  const versions = data?.sidecarVersions || []

  return (
    <span className={styles.sidecarEntry}>
      <a
        className={styles.sidecarFileLink}
        href={storeUrl(sidecar.downloadUrl)}
        download={sidecar.filename}
        onClick={(e) => e.stopPropagation()}
        title={`Download ${sidecar.filename}`}
      >
        {sidecar.filename}
      </a>
      <button
        type="button"
        className={styles.sidecarHistoryBtn}
        onClick={toggle}
        title="Earlier saved versions of this edit"
      >
        <HistoryIcon style={{ fontSize: '0.95rem' }} />
        {open ? 'hide' : 'versions'}
      </button>

      {open && (
        <span className={styles.sidecarVersionsPanel} onClick={(e) => e.stopPropagation()}>
        <span className={styles.sidecarVersions}>
          {loading && <span className={styles.sidecarVersionNote}>loading…</span>}
          {error && <span className={styles.sidecarVersionNote}>{error.message}</span>}
          {!loading && !error && versions.length === 0 && (
            <span className={styles.sidecarVersionNote}>no versions recorded</span>
          )}
          {versions.map((v) => (
            <span key={v.filename} className={styles.sidecarVersion}>
              <span className={styles.sidecarVersionWhen}>
                {formatWhen(v.savedAt)}
                {v.isCurrent && <em className={styles.sidecarVersionCurrent}>current</em>}
              </span>
              <span className={styles.sidecarVersionWho}>
                {/* A camera reading names nobody, because nobody worked on it.
                    "unattributed" would be wrong here too -- that means we do
                    not know who, and here we know there was no one. */}
                {v.editingWork === false
                  ? 'camera settings'
                  : (v.uploadedBy || 'unattributed')}
                {/* Which Lightroom catalogue it came from. Not in the file --
                    an .xmp names the application and no more -- so it is only
                    here when somebody said so at import. */}
                {v.source && <> · {v.source}</>}
                {' '}· {formatBytes(v.size)}
              </span>
              <a
                className={styles.sidecarVersionGet}
                href={storeUrl(v.downloadUrl)}
                download={v.filename}
                onClick={(e) => e.stopPropagation()}
              >
                <DownloadIcon style={{ fontSize: '0.9rem' }} /> get
              </a>
            </span>
          ))}
        </span>
        </span>
      )}
    </span>
  )
}

// A sidecar holding only what the camera recorded. Lightroom writes one for a
// photograph nobody has opened, and three fifths of the .xmp files in this
// library are exactly that.
const isCameraOnly = (sc) =>
  sc.editingWork === false ||
  (sc.editVersions === 0 && (sc.cameraVersions || 0) > 0)

export default function SidecarVersions({ sidecars, storeUrl, styles }) {
  const [showCamera, setShowCamera] = useState(false)
  if (!sidecars || sidecars.length === 0) return null

  // Kept apart on purpose. Listed together, a camera reading reads as one more
  // edit somebody made, which is the claim this whole distinction exists to
  // avoid. The files are still here, one click away.
  const edits = sidecars.filter((sc) => !isCameraOnly(sc))
  const camera = sidecars.filter(isCameraOnly)

  return (
    <>
      {edits.map((sc) => (
        <OneSidecar key={sc.path} sidecar={sc} storeUrl={storeUrl} styles={styles} />
      ))}

      {camera.length > 0 && (
        <>
          <button
            type="button"
            className={styles.cameraSettingsBtn}
            onClick={(e) => { e.stopPropagation(); setShowCamera((v) => !v) }}
            title="Settings the camera recorded — not anybody's edit"
          >
            <ApertureIcon style={{ fontSize: '0.95rem' }} />
            {showCamera ? 'hide' : `camera settings (${camera.length})`}
          </button>
          {showCamera && camera.map((sc) => (
            <OneSidecar key={sc.path} sidecar={sc} storeUrl={storeUrl} styles={styles} />
          ))}
        </>
      )}
    </>
  )
}
